/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import { Route, Routes, RouterProvider, createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { Suspense } from 'react';
import { lazyWithRetry } from 'utils/lazyImport';
import { useTranslation } from 'react-i18next';
import useScrollTop from 'core/hooks/useScrollTop';
import { SECTIONS } from 'constants/sections';
import { PasswordType } from './pages/auth/Password';
import { LoadingSpinner } from './components/Loading/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import LanguageLink from './pages/product/LanguageLink';

// Code-split imports for current (v2) routes
const ProductPage = lazyWithRetry(() => import('./pages/product/ProductPage'));
const LearnerPage = lazyWithRetry(() => import('./pages/learner/LearnerPage'));
const ModuleSharePage = lazyWithRetry(() => import('./pages/modules/Share/ModuleSharePage'));
const PagesV2 = lazyWithRetry(() => import('./pages/Home'));
const ModulesPage = lazyWithRetry(() => import('./pages/modules/ModulesPage'));
const CourseModulesPage = lazyWithRetry(() => import('./pages/course/Modules/CourseModulesPage'));
const CourseOverviewPage = lazyWithRetry(() => import('./pages/course/Overview/CourseOverviewPage'));
const AdminBoundary = lazyWithRetry(() => import('./features/AdminBoundary/AdminBoundary'));
const WorkFlowPage = lazyWithRetry(() => import('./pages/course/WorkFlow/WorkFlowPage'));
const CourseLearnersPage = lazyWithRetry(() => import('./pages/course/Learners/CourseLearners'));
const InviteCourseLearners = lazyWithRetry(() => import('./pages/course/Invites/InviteCourseLearners'));
const InviteCourseLearnerRemindersPage = lazyWithRetry(() => import('./pages/course/Invites/InviteReminders'));
const Integrations = lazyWithRetry(() => import('./pages/course/Integrations/CourseIntegrations'));
const IntegrationEdit = lazyWithRetry(() => import('./pages/course/Integrations/Edit/CourseIntegrationEdit'));
const IntegrationReport = lazyWithRetry(() => import('./pages/course/Integrations/Report/IntegrationReportPage'));
const SsoLinkPage = lazyWithRetry(() => import('./pages/course/Integrations/Sso/SsoLinkPage'));
const CourseCertificatePage = lazyWithRetry(() => import('./pages/course/Certificate/CourseCertificatePage'));
const CourseTeamPage = lazyWithRetry(() => import('./pages/course/Team/CourseTeamPage'));
const CourseReviewPage = lazyWithRetry(() => import('./pages/course/Review/CourseReviewPage'));
const CourseSourcesPage = lazyWithRetry(() => import('./pages/course/Sources/CourseSourcesPage'));
const InviteTeamDialog = lazyWithRetry(() => import('app/features/Course/CourseTeam/InviteTeamDialog'));
const NewModuleTypePage = lazyWithRetry(() => import('./pages/modules/New/Type/NewModuleTypePage'));
const ModuleImportPage = lazyWithRetry(() => import('./pages/modules/New/Import/ModuleImportPage'));
const LearnerProgressPage = lazyWithRetry(() => import('./pages/course/Learner/LearnerProgressPage'));
const LearnerEmailPage = lazyWithRetry(() => import('./pages/learner/Email/LearnerEmailPage'));
const LearnerVariablePage = lazyWithRetry(() => import('./pages/learner/Variable/LearnerVariablePage'));
const CoursesPage = lazyWithRetry(() => import('./pages/courses/CoursesPage'));
const NewCoursePage = lazyWithRetry(() => import('./pages/courses/New/NewCoursePage'));
const ModuleViewPage = lazyWithRetry(() => import('./pages/modules/View/ModuleViewPage'));
const ModuleEditPage = lazyWithRetry(() => import('./pages/modules/Edit/ModuleEditPage'));
const ThemePage = lazyWithRetry(() => import('./pages/settings/theme/ThemePage'));
const SettingsIntegrationsPage = lazyWithRetry(() => import('./pages/settings/integrations/IntegrationsPage'));
const TemplatesPage = lazyWithRetry(() => import('./pages/settings/templates/TemplatesPage'));
const CertificateDesignerPage = lazyWithRetry(() => import('./pages/settings/templates/CertificateDesignerPage'));
const EmailDesignerPage = lazyWithRetry(() => import('./pages/settings/templates/EmailDesignerPage'));
const ReceiptDesignerPage = lazyWithRetry(() => import('./pages/settings/templates/ReceiptDesignerPage'));
const DesignSystemPage = lazyWithRetry(() => import('./pages/settings/design-system/DesignSystemPage'));
const AccountPage = lazyWithRetry(() => import('./pages/settings/account/AccountPage'));
const SettingsLayout = lazyWithRetry(() => import('./pages/settings/SettingsLayout'));
const LearnersPage = lazyWithRetry(() => import('./pages/learners/LearnersPage'));
const ReportsPage = lazyWithRetry(() => import('./pages/reports/ReportsPage'));
const RequireOrgAdmin = lazyWithRetry(() => import('./pages/admins/RequireOrgAdmin'));
const PlatformAdminsPage = lazyWithRetry(() => import('./pages/admins/PlatformAdminsPage'));
const InvitePlatformAdmin = lazyWithRetry(() => import('./pages/admins/InvitePlatformAdmin'));
const VariableReportPage = lazyWithRetry(() => import('./pages/course/Integrations/Report/VariableReportPage'));
const ExamplesPage = lazyWithRetry(() => import('./pages/examples/ExamplesPage'));
const CourseNodePreviewPage = lazyWithRetry(() => import('./pages/course/CourseNode/CourseNodePage'));
const SceneViewerPage = lazyWithRetry(() => import('./pages/course/Scene/SceneViewerPage'));

// Layout routes for the rebuilt admin shell
const AdminShellLayout = lazyWithRetry(() => import('./pages/_layouts/AdminShellLayout'));
const CourseShellLayout = lazyWithRetry(() => import('./pages/_layouts/CourseShellLayout'));
const FullScreenLayout = lazyWithRetry(() => import('./pages/_layouts/FullScreenLayout'));
const OnboardingPage = lazyWithRetry(() => import('./pages/onboarding/OnboardingPage'));

// Code-split imports for auth/public routes
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const LogIn = lazyWithRetry(() => import('./pages/auth/LogIn').then(m => ({ default: m.LogIn })));
const Password = lazyWithRetry(() => import('./pages/auth/Password').then(m => ({ default: m.Password })));
const ForgotPassword = lazyWithRetry(() =>
  import('./pages/auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })),
);
const SignUp = lazyWithRetry(() => import('./pages/auth/SignUp').then(m => ({ default: m.SignUp })));
const New = lazyWithRetry(() => import('./pages/auth/New').then(m => ({ default: m.New })));
const OAuthConsentPage = lazyWithRetry(() => import('./pages/oauth/OAuthConsentPage'));

/**
 * Redirects a legacy in-course URL to its new section, preserving :courseId.
 * e.g. /course/12/admins -> /course/12/team
 */
function LegacyCourseRedirect({ to }: { to: string }) {
  const { courseId } = useParams();
  return <Navigate to={`/${SECTIONS.COURSE}/${courseId}/${to}`} replace />;
}

const router = createBrowserRouter([{ path: '*', Component: Root }]);

export function App() {
  return (
    <>
      <RouterProvider router={router} />
      <video id="videoEl" className="hide-video" playsInline muted webkit-playsinline="true" />
      {/* Scene video behind a voiceover — always muted so it plays without a gesture. */}
      <video id="bgVideoEl" className="hide-video" playsInline muted webkit-playsinline="true" />
    </>
  );
}

export function Root() {
  useTranslation();
  useScrollTop();
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="w-full h-full flex justify-center items-center">
            <LoadingSpinner />
          </div>
        }
      >
        <Routes>
          {/* `:lang` picks the language each module plays in (see LanguageLink); `en` is as written. */}
          <Route
            path={`:lang/:token`}
            element={
              <LanguageLink notFound={<NotFoundPage />}>
                <ProductPage />
              </LanguageLink>
            }
          />
          <Route
            path={`scorm/:lang/:token`}
            element={
              <LanguageLink notFound={<NotFoundPage />}>
                <ProductPage scorm={true} />
              </LanguageLink>
            }
          />
          <Route
            path={`:lang/:token/:entityToken`}
            element={
              <LanguageLink notFound={<NotFoundPage />}>
                <ProductPage />
              </LanguageLink>
            }
          />
          <Route path={`invite/:inviteToken`} element={<ProductPage />} />
          <Route path={`sso/:inviteToken/:token`} element={<ProductPage />} />
          <Route path={`invite/:inviteToken/:entityToken`} element={<ProductPage />} />
          <Route path={`token/:token/:entityToken`} element={<ProductPage />} />
          {/* Outside AdminBoundary: any allowlisted user, learners included, may connect an app. */}
          <Route path="oauth/consent" element={<OAuthConsentPage />} />
          {/* Current version routes (modern pages) */}
          <Route path={'/'} element={<AdminBoundary />}>
            {/* ---------- Chrome-bearing routes: persistent sidebar + top bar ---------- */}
            <Route element={<AdminShellLayout />}>
              <Route index={true} element={<PagesV2 />} />
              <Route path={SECTIONS.MODULES}>
                <Route index={true} element={<ModulesPage isHome />} />
                <Route path={`new/import`} element={<ModuleImportPage />} />
              </Route>
              <Route path={SECTIONS.COURSES} element={<CoursesPage />} />
              <Route path={SECTIONS.EXAMPLES} element={<ExamplesPage />} />
              <Route path={`${SECTIONS.COURSE}/new`} element={<NewCoursePage />} />
              <Route path={SECTIONS.LEARNERS} element={<LearnersPage />} />
              <Route path={`${SECTIONS.LEARNER}/:userId`} element={<LearnerPage />} />
              <Route path={SECTIONS.VARIABLES} element={<ReportsPage />} />
              <Route path="design-system" element={<DesignSystemPage />} />

              {/* Settings */}
              <Route path={SECTIONS.SETTINGS} element={<SettingsLayout />}>
                <Route index={true} element={<Navigate to={SECTIONS.ACCOUNT} replace />} />
                <Route path={SECTIONS.ACCOUNT} element={<AccountPage />} />
                <Route path={SECTIONS.THEME} element={<ThemePage />} />
                <Route path={SECTIONS.TEMPLATES} element={<TemplatesPage />} />
                <Route path={SECTIONS.INTEGRATIONS}>
                  <Route index={true} element={<SettingsIntegrationsPage />} />
                  <Route path={`:orgSystemId/edit`} element={<IntegrationEdit />} />
                </Route>
                <Route element={<RequireOrgAdmin />}>
                  <Route path={SECTIONS.ADMINISTRATORS} element={<PlatformAdminsPage />} />
                  <Route path={`${SECTIONS.ADMINISTRATORS}/invite`} element={<InvitePlatformAdmin />} />
                </Route>
              </Route>
            </Route>

            {/* In-course sections. A SIBLING of AdminShellLayout, not a child:
                CourseShellLayout renders its own AppShell (with the section rail
                in place of the global one), and nesting the two would mount two
                shells, two sidebars and two top bars. */}
            <Route path={`${SECTIONS.COURSE}/:courseId`} element={<CourseShellLayout />}>
              <Route index={true} element={<Navigate to={SECTIONS.OVERVIEW} replace />} />
              <Route path={SECTIONS.OVERVIEW} element={<CourseOverviewPage />} />
              <Route path={SECTIONS.MODULES}>
                <Route index={true} element={<CourseModulesPage />} />
                <Route path={`new/import`} element={<ModuleImportPage />} />
              </Route>
              <Route path={SECTIONS.LEARNERS}>
                <Route index={true} element={<CourseLearnersPage />} />
                <Route path={`progress/:courseProgressId`}>
                  <Route index={true} element={<LearnerProgressPage />} />
                  <Route path={`email/:emailId`} element={<LearnerEmailPage />} />
                  <Route path={`variable/:orgUserVariableId`} element={<LearnerVariablePage />} />
                </Route>
              </Route>
              <Route path={SECTIONS.INTEGRATIONS}>
                <Route index={true} element={<Integrations />} />
                <Route path={`:orgSystemId/edit`} element={<IntegrationEdit />} />
                <Route path={`:orgSystemId/report`} element={<IntegrationReport />} />
                <Route path={`sso/:courseNodeId`} element={<SsoLinkPage />} />
              </Route>
              <Route path={SECTIONS.CERTIFICATE} element={<CourseCertificatePage />} />
              <Route path={SECTIONS.REVIEW} element={<CourseReviewPage />} />
              <Route path={SECTIONS.SOURCES} element={<CourseSourcesPage />} />
              {/* Frame 30b opens the invite as a sheet over the team list, so
                  the child route renders inside CourseTeamPage's outlet rather
                  than replacing it. */}
              <Route path={SECTIONS.TEAM} element={<CourseTeamPage />}>
                <Route path={`invite`} element={<InviteTeamDialog />} />
              </Route>
              <Route path={`${SECTIONS.VARIABLES}/:variableId`} element={<VariableReportPage />} />
              <Route path={SECTIONS.INVITES}>
                <Route index={true} element={<Navigate to={`../${SECTIONS.LEARNERS}`} replace />} />
                <Route path={`new`} element={<InviteCourseLearners />} />
                <Route path={`reminders`} element={<InviteCourseLearnerRemindersPage />} />
              </Route>
            </Route>

            {/* ---------- Full-screen: no shell, each page owns its top bar ---------- */}
            <Route element={<FullScreenLayout />}>
              {/* Onboarding a new workspace, straight after sign-up (design 05–08). */}
              <Route path={SECTIONS.WELCOME} element={<OnboardingPage />} />
              {/* Certificate and email designers (TP-02, TP-05/06). */}
              <Route
                path={`${SECTIONS.SETTINGS}/${SECTIONS.TEMPLATES}/certificates/:templateId`}
                element={<CertificateDesignerPage />}
              />
              <Route
                path={`${SECTIONS.SETTINGS}/${SECTIONS.TEMPLATES}/emails/:emailKey`}
                element={<EmailDesignerPage />}
              />
              <Route path={`${SECTIONS.SETTINGS}/${SECTIONS.TEMPLATES}/receipts`} element={<ReceiptDesignerPage />} />
              <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.WORKFLOW}`} element={<WorkFlowPage />} />
              {/* The name gate renders on the editor, so it is full-screen
                  too — see frame 40. Static `new` outranks `:moduleId`. */}
              <Route
                path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/new/:type`}
                element={<NewModuleTypePage />}
              />
              <Route path={`${SECTIONS.MODULES}/new/:type`} element={<NewModuleTypePage />} />
              <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/:moduleId`} element={<ModuleEditPage />} />
              <Route
                path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/:moduleId/preview`}
                element={<ModuleViewPage />}
              />
              {/* An example (TEMPLATE) module, which may be another org's — no course. */}
              <Route path={`${SECTIONS.MODULES}/:moduleId/preview`} element={<ModuleViewPage />} />
              <Route
                path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/${SECTIONS.SCENE}/:sceneId`}
                element={<SceneViewerPage />}
              />
              <Route
                path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/share`}
                element={<ModuleSharePage />}
              />
              <Route
                path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/preview`}
                element={<CourseNodePreviewPage />}
              />
              <Route
                path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/:mode`}
                element={<CourseNodePreviewPage />}
              />
            </Route>

            {/* ---------- Legacy URL redirects. Keep for >=1 release. ---------- */}
            <Route
              path={SECTIONS.ACCOUNT}
              element={<Navigate to={`/${SECTIONS.SETTINGS}/${SECTIONS.ACCOUNT}`} replace />}
            />
            <Route
              path={SECTIONS.THEME}
              element={<Navigate to={`/${SECTIONS.SETTINGS}/${SECTIONS.THEME}`} replace />}
            />
            <Route
              path={SECTIONS.ADMINISTRATORS}
              element={<Navigate to={`/${SECTIONS.SETTINGS}/${SECTIONS.ADMINISTRATORS}`} replace />}
            />
            <Route
              path={`${SECTIONS.ADMINISTRATORS}/invite`}
              element={<Navigate to={`/${SECTIONS.SETTINGS}/${SECTIONS.ADMINISTRATORS}/invite`} replace />}
            />
            <Route
              path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.CERTIFICATES}`}
              element={<LegacyCourseRedirect to={SECTIONS.CERTIFICATE} />}
            />
            <Route
              path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.ADMINISTRATORS}`}
              element={<LegacyCourseRedirect to={SECTIONS.TEAM} />}
            />
            <Route
              path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.ADMINISTRATORS}/invite`}
              element={<LegacyCourseRedirect to={`${SECTIONS.TEAM}/invite`} />}
            />
          </Route>

          {/* Public routes and special routes that don't need versioning */}
          <Route path={'manager'} element={<LogIn />} />

          {/* Authentication routes */}
          <Route path={`hello/:token`} element={<Password type={PasswordType.RESET} />} />
          <Route path={`hello/:token/:returnToken`} element={<Password type={PasswordType.RESET} />} />
          <Route path={`reset/:token`} element={<Password type={PasswordType.RESET} />} />
          <Route path={`forgot`} element={<ForgotPassword />} />
          <Route path={`signup`} element={<SignUp />} />
          <Route path={'new/:token'} element={<New />} />
          <Route path={'new/:token/:courseId/:moduleId'} element={<New />} />

          {/* 404 fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
